import React, { useState, useEffect, useRef, Fragment } from 'react'
import QuestHOC from './QuestHOC'
import FacultyCard from './FacultyCard'
import FacultyEditor from './FacultyEditor'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const truncateLabel = (text) => {
  const t = String(text || '').trim()
  if (t.length > 72) return `${t.slice(0, 69)}\u2026`
  return t
}

// Function to create a fresh faculty
const createFreshFaculty = (sequenceNumber = 1) => ({
  name: '',
  title: '',
  expertise: '',
  description: '',
  imageFileName: '',
  isUploading: false,
  isVisible: true,
  sequenceNumber: sequenceNumber,
})

// Function to clean faculty data for API (remove UI-only fields)
const cleanFacultyForAPI = (faculty) => {
  const { isUploading, _tempId, ...cleanFaculty } = faculty
  return cleanFaculty
}

const hasFacultyContent = (faculty) =>
  !!(
    faculty?.name?.trim() ||
    faculty?.title?.trim() ||
    faculty?.expertise?.trim() ||
    faculty?.description?.trim() ||
    faculty?.imageFileName
  )

const QuestFaculty = ({
  questDetail,
  updateQuestDetails,
  isFormSubmitting,
}) => {
  // Faculty sections state
  const [faculties, setFaculties] = useState([])
  const facultiesRef = useRef(faculties)

  // Inline editor state (replaces modal)
  const [facultyEditor, setFacultyEditor] = useState({
    open: false,
    faculty: null,
    facultyIndex: null,
  })
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    index: null,
    label: '',
  })

  useEffect(() => {
    facultiesRef.current = faculties
  }, [faculties])

  useEffect(() => {
    const facultyData = questDetail?.faculties
    if (facultyData?.length > 0) {
      // Add unique IDs and sequence numbers for tracking if they don't exist
      const facultiesWithIds = facultyData.map((faculty, index) => ({
        ...faculty,
        _tempId: faculty._tempId || `faculty-${Date.now()}-${index}`,
        sequenceNumber: faculty.sequenceNumber || index + 1,
      }))
      setFaculties(facultiesWithIds)
    } else {
      setFaculties([])
    }
  }, [questDetail])

  useEffect(() => {
    if (!facultyEditor.open) {
      return undefined
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeFacultyEditor()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [facultyEditor.open])

  const closeFacultyEditor = () => {
    setFacultyEditor({
      open: false,
      faculty: null,
      facultyIndex: null,
    })
  }

  const requestFacultyDelete = (idx) => {
    const faculty = faculties[idx]
    setDeleteModal({
      show: true,
      index: idx,
      label: faculty?.name?.trim() || faculty?.title?.trim() || 'Untitled',
    })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, index: null, label: '' })
  }

  const confirmFacultyDelete = () => {
    const idx = deleteModal.index
    closeDeleteModal()
    if (typeof idx !== 'number') return
    if (facultyEditor.open && facultyEditor.facultyIndex === idx) {
      closeFacultyEditor()
    }
    const newFaculties = faculties.filter((_, index) => index !== idx)
    const updatedFaculties = newFaculties.map((faculty, index) => ({
      ...faculty,
      sequenceNumber: index + 1,
    }))

    setFaculties(updatedFaculties)
    updateQuestDetails({
      faculties: updatedFaculties.map(cleanFacultyForAPI),
    })
  }

  // Function to handle faculty card click (open inline editor under row)
  const handleFacultyEdit = (faculty, index) => {
    if (facultyEditor.open && facultyEditor.facultyIndex === index) {
      closeFacultyEditor()
      return
    }
    setFacultyEditor({
      open: true,
      faculty: faculty,
      facultyIndex: index,
    })
  }

  // Function to handle adding a new faculty - opens blank add-form at bottom
  const handleAddFaculty = () => {
    const freshFaculty = createFreshFaculty(faculties.length + 1)

    setFacultyEditor({
      open: true,
      faculty: freshFaculty,
      facultyIndex: faculties.length, // New faculty index
    })
  }

  const adjustEditingIndex = (dragIndex, hoverIndex, currentIndex) => {
    if (typeof currentIndex !== 'number') {
      return currentIndex
    }
    if (currentIndex === dragIndex) {
      return hoverIndex
    }
    if (dragIndex < currentIndex && hoverIndex >= currentIndex) {
      return currentIndex - 1
    }
    if (dragIndex > currentIndex && hoverIndex <= currentIndex) {
      return currentIndex + 1
    }
    return currentIndex
  }

  const handleFacultyMove = (dragIndex, hoverIndex) => {
    const nextFaculties = [...facultiesRef.current]
    const [moved] = nextFaculties.splice(dragIndex, 1)
    nextFaculties.splice(hoverIndex, 0, moved)
    const updatedFaculties = nextFaculties.map((faculty, index) => ({
      ...faculty,
      sequenceNumber: index + 1,
    }))

    facultiesRef.current = updatedFaculties
    setFaculties(updatedFaculties)

    if (facultyEditor.open) {
      const nextIndex = adjustEditingIndex(
        dragIndex,
        hoverIndex,
        facultyEditor.facultyIndex,
      )
      if (nextIndex !== facultyEditor.facultyIndex) {
        setFacultyEditor((prev) => ({
          ...prev,
          facultyIndex: nextIndex,
          faculty:
            typeof nextIndex === 'number'
              ? updatedFaculties[nextIndex]
              : prev.faculty,
        }))
      }
    }
  }

  const persistAfterReorder = () => {
    updateQuestDetails({
      faculties: facultiesRef.current.map(cleanFacultyForAPI),
    })
  }

  const handleFacultySave = (facultyData) => {
    const { facultyIndex } = facultyEditor
    const isNew = facultyIndex >= faculties.length

    if (isNew && !hasFacultyContent(facultyData)) {
      closeFacultyEditor()
      return
    }

    if (facultyIndex < faculties.length) {
      const updatedFaculties = [...faculties]
      updatedFaculties[facultyIndex] = {
        ...facultyData,
        _tempId: updatedFaculties[facultyIndex]._tempId,
        sequenceNumber: facultyIndex + 1,
      }
      setFaculties(updatedFaculties)
      updateQuestDetails({
        faculties: updatedFaculties.map(cleanFacultyForAPI),
      })
    } else {
      const newFaculties = [
        ...faculties,
        {
          ...facultyData,
          _tempId: `faculty-${Date.now()}-${faculties.length}`,
          sequenceNumber: faculties.length + 1,
        },
      ]
      setFaculties(newFaculties)
      updateQuestDetails({
        faculties: newFaculties.map(cleanFacultyForAPI),
      })
    }
    closeFacultyEditor()
  }

  const isAdding =
    facultyEditor.open && facultyEditor.facultyIndex >= faculties.length

  const deleteName = truncateLabel(deleteModal.label)
  const deleteMessage = deleteName
    ? `\u201C${deleteName}\u201D will be removed. They will be removed from this experience.`
    : 'They will be removed from this experience.'

  return (
    <div className='quest-faculty-sections'>
      {faculties.length === 0 && !facultyEditor.open ? null : (
        <div className='faculty-sections-list' data-add-list='faculty'>
          {faculties.map((faculty, index) => {
            const isEditing =
              facultyEditor.open && facultyEditor.facultyIndex === index
            return (
              <Fragment key={faculty._tempId || index}>
                <FacultyCard
                  faculty={faculty}
                  index={index}
                  isEditing={isEditing}
                  onEdit={handleFacultyEdit}
                  onDelete={() => requestFacultyDelete(index)}
                  moveItem={handleFacultyMove}
                  onDragEnd={persistAfterReorder}
                  isUploading={faculty.isUploading}
                />
                {isEditing ? (
                  <div className='experience-add-form'>
                    <FacultyEditor
                      key={faculty._tempId || `edit-${index}`}
                      faculty={facultyEditor.faculty}
                      facultyIndex={facultyEditor.facultyIndex}
                      onSave={handleFacultySave}
                      onCancel={closeFacultyEditor}
                      isSaving={isFormSubmitting}
                    />
                  </div>
                ) : null}
              </Fragment>
            )
          })}

          {isAdding ? (
            <div className='experience-add-form'>
              <FacultyEditor
                key='new-faculty'
                faculty={facultyEditor.faculty}
                facultyIndex={facultyEditor.facultyIndex}
                onSave={handleFacultySave}
                onCancel={closeFacultyEditor}
                isSaving={isFormSubmitting}
              />
            </div>
          ) : null}
        </div>
      )}

      {!facultyEditor.open ? (
        <div className='experience-form-actions'>
          <button
            type='button'
            className='experience-btn-sm btn-outline-experience'
            onClick={handleAddFaculty}>
            Add Faculty
          </button>
        </div>
      ) : null}

      <ExperienceConfirmModal
        show={deleteModal.show}
        onHide={closeDeleteModal}
        onConfirm={confirmFacultyDelete}
        title='Delete this faculty member?'
        message={deleteMessage}
        confirmLabel='Delete'
        isLoading={isFormSubmitting}
      />
    </div>
  )
}

export default QuestHOC(QuestFaculty)
